import { DateTime } from "luxon";

const DEFAULT_TIMEZONE = "America/Toronto";
const WEEKDAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

function calendarDate(value) {
  // PostgreSQL DATE values arrive as local-midnight Dates; API dates are strings.
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toISODate();
  }
  if (typeof value !== "string") return null;
  const isoDate = /^\d{4}-\d{2}-\d{2}/.exec(value);
  if (isoDate) return isoDate[0];
  return DateTime.fromFormat(value, "LLLL d, yyyy", {
    locale: "en-US", zone: "UTC"
  }).toISODate();
}

function lastStartMinute(program, weekday) {
  const times = program.enableCustomTimes
    ? (program.customTimes || {})[weekday] || []
    : program.timing || [];
  const starts = times
    .filter((value, index) => index % 2 === 0 && value !== null && value !== "")
    .map(Number)
    .filter(value => Number.isFinite(value) && value >= 0 && value < 24)
    .map(value => Math.round(value * 60));
  // Preserve the existing midnight fallback for legacy schedules without times.
  return starts.length ? Math.max(...starts) : 0;
}

/**
 * Keep this policy in sync with the backend lib/programProration.js and the other frontend.
 * A scheduled day remains billable until its last class starts, in the
 * organization's timezone. Multiple time slots still count as one paid day.
 */
export function calculateProratedClasses(program, totalPrice, now = new Date()) {
  const start = calendarDate(program.program_start);
  const weeks = Number(program.number_of_weeks);
  const empty = { totalSessions: 0, sessionsPassed: 0, amount: 0 };
  if (!start || !Number.isFinite(weeks) || weeks <= 0) return empty;

  // Iterate calendar days in UTC so the browser/server timezone and DST cannot
  // shift weekdays or the number of scheduled days.
  let date = DateTime.fromISO(start, { zone: "UTC" });
  if (!date.isValid) return empty;
  const end = date.plus({ weeks });
  const zone = program.timezone || DEFAULT_TIMEZONE;
  if (!DateTime.fromJSDate(now, { zone }).isValid) {
    throw new RangeError("A valid organization timezone is required for proration.");
  }
  const nowMillis = now.valueOf();
  const closureDates = new Set((program.closure_dates || []).map(calendarDate));
  const closureRange = program.closure_timeframe || [];
  const closureStart = calendarDate(closureRange[0]);
  const closureEnd = calendarDate(closureRange[1]);
  let totalSessions = 0;
  let sessionsPassed = 0;

  while (date < end) {
    const dateKey = date.toISODate();
    const weekday = WEEKDAYS[date.weekday - 1];
    const isClosure = closureDates.has(dateKey) ||
      (closureStart && closureEnd && dateKey >= closureStart && dateKey <= closureEnd);
    if ((program.days_of_week || {})[weekday] && !isClosure) {
      const startMinute = lastStartMinute(program, weekday);
      const cutoff = DateTime.fromISO(dateKey, { zone }).set({
        hour: Math.floor(startMinute / 60),
        minute: startMinute % 60,
        second: 0,
        millisecond: 0
      });
      if (cutoff.toMillis() <= nowMillis) sessionsPassed++;
      totalSessions++;
    }
    date = date.plus({ days: 1 });
  }

  return {
    totalSessions,
    sessionsPassed,
    amount: totalSessions ? Number(totalPrice) * (totalSessions - sessionsPassed) / totalSessions : 0
  };
}
